import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { database, firestore, storage } from '../../firebase/config';
import { ref, onValue, off, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import ChatService from '../../services/ChatService';
import { toast } from 'react-hot-toast';

const BusinessChat = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [userProfiles, setUserProfiles] = useState({});
  const [applicants, setApplicants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const initializeData = async () => {
      if (currentUser) {
        console.log(`🚀 [Chat] Initializing data for user:`, currentUser.uid);
        await loadBusinessData();
      }
    };
    
    initializeData();
  }, [currentUser]);

  useEffect(() => {
    // Only load chats after we have current user and applicants have been loaded (including empty array)
    if (currentUser && Array.isArray(applicants)) {
      console.log(`🔄 [Chat] Applicants updated, reloading chats. Applicants:`, applicants);
      loadChats();
    }
  }, [currentUser, applicants]);

  useEffect(() => {
    if (selectedChat) {
      loadMessages();
    }
  }, [selectedChat]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const roomId = searchParams.get('room');
    
    if (roomId && chats.length > 0) {
      const targetChat = chats.find(chat => chat.id === roomId);
      if (targetChat) {
        setSelectedChat(targetChat);
        navigate('/business-dashboard/chat', { replace: true });
      }
    }
  }, [location.search, chats, navigate]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadBusinessData = async () => {
    try {
      console.log(`🏢 [Chat] Loading business data for user:`, currentUser.uid);
      
      const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().businessId) {
        const businessId = userDoc.data().businessId;
        console.log(`🏢 [Chat] Found business ID:`, businessId);
        
        const applicationsQuery = query(
          collection(firestore, 'jobApplications'),
          where('businessId', '==', businessId)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        const applicantIds = [...new Set(applicationsSnapshot.docs.map(doc => doc.data().userId))];
        
        console.log(`👥 [Chat] Found ${applicantIds.length} unique applicants:`, applicantIds);
        
        setApplicants(applicantIds);
      } else {
        console.log(`❌ [Chat] No business ID found for user`);
        setApplicants([]);
      }
    } catch (error) {
      console.error('Error loading business data:', error);
      setApplicants([]);
    }
  };

  const loadUserProfile = async (userId) => {
    if (userProfiles[userId]) return userProfiles[userId];
    
    try {
      let profileData = null;
      
      console.log(`🔍 [Chat] Fetching profile for user ${userId}...`);
      
      // First try userProfiles collection (same as regular user)
      const profileDoc = await getDoc(doc(firestore, 'userProfiles', userId));
      if (profileDoc.exists()) {
        profileData = profileDoc.data();
        console.log(`✅ [Chat] Found profile in userProfiles for ${userId}:`, {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          hasImagePath: !!profileData.profileImagePath,
          hasImageUrl: !!profileData.profileImageUrl
        });

        // Apply same image logic as regular user
        let finalImageUrl = null;
        
        // Check profileImagePath first (regular user method)
        if (profileData.profileImagePath) {
          if (profileData.profileImagePath.startsWith('http')) {
            // Direct HTTP URL
            finalImageUrl = profileData.profileImagePath;
            console.log(`🔗 [Chat] Using direct HTTP URL for ${userId}:`, finalImageUrl);
          } else {
            try {
              // Try Firebase Storage
              const { ref, getDownloadURL } = await import('firebase/storage');
              const { storage } = await import('../../firebase/config');
              
              const storagePath = profileData.profileImagePath.startsWith('userProfilePhoto/') 
                ? profileData.profileImagePath 
                : `userProfilePhoto/${userId}`;
              
              const imageRef = ref(storage, storagePath);
              finalImageUrl = await getDownloadURL(imageRef);
              console.log(`☁️ [Chat] Got Firebase Storage URL for ${userId}:`, finalImageUrl);
            } catch (imageError) {
              console.log(`❌ [Chat] Firebase Storage failed for ${userId}, trying local asset:`, imageError);
              // Fallback to local asset path
              if (profileData.profileImagePath && !profileData.profileImagePath.startsWith('profile_images/')) {
                finalImageUrl = `/asset/${profileData.profileImagePath}`;
                console.log(`📁 [Chat] Using local asset for ${userId}:`, finalImageUrl);
              }
            }
          }
        }
        
        // If no profileImagePath, try profileImageUrl
        if (!finalImageUrl && profileData.profileImageUrl) {
          finalImageUrl = profileData.profileImageUrl;
          console.log(`🔗 [Chat] Using profileImageUrl for ${userId}:`, finalImageUrl);
        }
        
        // Set the final image URL
        profileData.profileImageUrl = finalImageUrl;
        
      } else {
        console.log(`❌ [Chat] No profile in userProfiles for ${userId}, checking users collection...`);
        
        // Fallback to users collection
        const userDoc = await getDoc(doc(firestore, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          profileData = {
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            phone: userData.phone || '',
            bio: userData.bio || '',
            skills: userData.skills || '',
            experience: userData.experience || '',
            education: userData.education || '',
            availability: userData.availability || '',
            location: userData.location || '',
            // Apply same logic for users collection
            profileImagePath: userData.profileImagePath || '',
            profileImageUrl: userData.profileImageUrl || 
                           userData.photoURL || 
                           userData.profilePicture || 
                           userData.avatar || 
                           userData.image || 
                           userData.picture || ''
          };
          
          // Apply same image processing for users collection
          let finalImageUrl = null;
          
          if (profileData.profileImagePath) {
            if (profileData.profileImagePath.startsWith('http')) {
              finalImageUrl = profileData.profileImagePath;
            } else {
              try {
                const { ref, getDownloadURL } = await import('firebase/storage');
                const { storage } = await import('../../firebase/config');
                
                const storagePath = profileData.profileImagePath.startsWith('userProfilePhoto/') 
                  ? profileData.profileImagePath 
                  : `userProfilePhoto/${userId}`;
                
                const imageRef = ref(storage, storagePath);
                finalImageUrl = await getDownloadURL(imageRef);
              } catch (imageError) {
                if (profileData.profileImagePath && !profileData.profileImagePath.startsWith('profile_images/')) {
                  finalImageUrl = `/asset/${profileData.profileImagePath}`;
                }
              }
            }
          }
          
          if (!finalImageUrl && profileData.profileImageUrl) {
            finalImageUrl = profileData.profileImageUrl;
          }
          
          profileData.profileImageUrl = finalImageUrl;
          
          console.log(`✅ [Chat] Found fallback profile in users for ${userId}:`, {
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            hasImagePath: !!userData.profileImagePath,
            hasImageUrl: !!finalImageUrl,
            finalImageUrl: finalImageUrl
          });
        } else {
          console.log(`❌ [Chat] No profile found anywhere for user ${userId}`);
        }
      }
      
      let applicationContext = null;
      if (profileData && applicants.includes(userId)) {
        try {
          const currentUserDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
          if (currentUserDoc.exists() && currentUserDoc.data().businessId) {
            const businessId = currentUserDoc.data().businessId;
            
            const applicationsQuery = query(
              collection(firestore, 'jobApplications'),
              where('userId', '==', userId),
              where('businessId', '==', businessId)
            );
            const applicationsSnapshot = await getDocs(applicationsQuery);
            
            if (!applicationsSnapshot.empty) {
              const applications = applicationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              }));
              
              const jobPromises = applications.map(async (app) => {
                if (app.jobId) {
                  const jobDoc = await getDoc(doc(firestore, 'jobs', app.jobId));
                  return jobDoc.exists() ? jobDoc.data().title : 'Unknown Position';
                }
                return 'Unknown Position';
              });
              
              const jobTitles = await Promise.all(jobPromises);
              applicationContext = {
                totalApplications: applications.length,
                latestApplication: applications[0],
                jobTitles: jobTitles,
                positions: jobTitles.join(', ')
              };
            }
          }
        } catch (error) {
          console.log('Error loading application context:', error);
        }
      }
      
      if (profileData) {
        const profile = {
          name: `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || 'Unknown User',
          avatar: profileData.profileImageUrl || null,
          email: profileData.email || '',
          phone: profileData.phone || '',
          bio: profileData.bio || '',
          skills: profileData.skills || '',
          experience: profileData.experience || '',
          education: profileData.education || '',
          availability: profileData.availability || '',
          location: profileData.location || '',
          applicationContext: applicationContext,
          userType: applicants.includes(userId) ? 'Job Applicant' : 'Contact'
        };
        
        console.log(`📊 [Chat] Final profile for ${userId}:`, {
          name: profile.name,
          hasAvatar: !!profile.avatar,
          avatarUrl: profile.avatar,
          userType: profile.userType
        });
        
        setUserProfiles(prev => ({ ...prev, [userId]: profile }));
        return profile;
      }
    } catch (error) {
      console.error(`❌ [Chat] Error loading user profile for ${userId}:`, error);
    }
    
    console.log(`⚠️ [Chat] Returning default profile for ${userId}`);
    return { name: 'Unknown User', avatar: null, email: '', userType: 'Contact' };
  };

  const loadChats = () => {
    console.log(`🔍 [Chat] Loading chats with applicants array:`, applicants);
    
    const chatRoomsRef = ref(database, 'chatRooms');
    const unsubscribe = onValue(chatRoomsRef, async (snapshot) => {
      const chatRoomsData = snapshot.val();
      if (!chatRoomsData) {
        setChats([]);
        setLoading(false);
        return;
      }

      // Get fresh applicants data to avoid stale closure
      let currentApplicants = [];
      try {
        const userDoc = await getDoc(doc(firestore, 'users', currentUser.uid));
        if (userDoc.exists() && userDoc.data().businessId) {
          const businessId = userDoc.data().businessId;
          const applicationsQuery = query(
            collection(firestore, 'jobApplications'),
            where('businessId', '==', businessId)
          );
          const applicationsSnapshot = await getDocs(applicationsQuery);
          currentApplicants = [...new Set(applicationsSnapshot.docs.map(doc => doc.data().userId))];
          console.log(`🔄 [Chat] Fresh applicants in loadChats:`, currentApplicants);
        }
      } catch (error) {
        console.error('Error getting fresh applicants:', error);
        currentApplicants = applicants; // fallback to state
      }

      const userChats = [];
      for (const [roomId, room] of Object.entries(chatRoomsData)) {
        if (room.participants && room.participants[currentUser.uid]) {
          const otherUserId = Object.keys(room.participants).find(id => id !== currentUser.uid);
          if (otherUserId) {
            const otherUserProfile = await loadUserProfile(otherUserId);
            const isApplicant = currentApplicants.includes(otherUserId);
            
            console.log(`👤 [Chat] User ${otherUserId} (${otherUserProfile.name}):`, {
              isApplicant: isApplicant,
              inCurrentApplicantsList: currentApplicants.includes(otherUserId),
              currentApplicantsArray: currentApplicants,
              stateApplicantsArray: applicants
            });
            
            userChats.push({
              id: roomId,
              name: otherUserProfile.name,
              avatar: otherUserProfile.avatar,
              email: otherUserProfile.email,
              lastMessage: room.lastMessage || '',
              lastMessageTimestamp: room.lastMessageTimestamp || 0,
              unreadCount: room.unreadCount || {},
              otherUserId,
              isApplicant,
              participants: room.participants
            });
          }
        }
      }

      userChats.sort((a, b) => (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0));
      
      console.log(`📊 [Chat] Final chat list:`, userChats.map(chat => ({
        name: chat.name,
        isApplicant: chat.isApplicant,
        otherUserId: chat.otherUserId
      })));
      
      setChats(userChats);
      setLoading(false);
    });

    return () => off(chatRoomsRef, 'value', unsubscribe);
  };

  const markAsRead = async (chatId) => {
    try {
      const unreadCountRef = ref(database, `chatRooms/${chatId}/unreadCount/${currentUser.uid}`);
      await set(unreadCountRef, 0);
      console.log(`✅ [Chat] Marked chat ${chatId} as read for user ${currentUser.uid}`);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const loadMessages = () => {
    const messagesRef = ref(database, `chatRooms/${selectedChat.id}/messages`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const messagesData = snapshot.val();
      if (messagesData) {
        const messagesList = Object.entries(messagesData)
          .map(([id, message]) => ({ id, ...message }))
          .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
        setMessages(messagesList);
      } else {
        setMessages([]);
      }
    });

    // Mark messages as read when loading them
    markAsRead(selectedChat.id);

    return () => off(messagesRef, 'value', unsubscribe);
  };

  const sendMessage = async (text, file = null) => {
    if ((!text.trim() && !file) || !selectedChat || sending) return;

    setSending(true);
    try {
      if (file) {
        const fileRef = storageRef(storage, `chat_files/${selectedChat.id}/${Date.now()}_${file.name}`);
        const uploadResult = await uploadBytes(fileRef, file);
        const fileUrl = await getDownloadURL(uploadResult.ref);
        
        await ChatService.sendMessage(
          selectedChat.id, 
          currentUser.uid, 
          text.trim() || `Sent a ${file.type.startsWith('image/') ? 'photo' : 'file'}`,
          fileUrl,
          file.name,
          file.type
        );
      } else {
        await ChatService.sendMessage(selectedChat.id, currentUser.uid, text.trim());
      }
      
      setMessageText('');
      toast.success('Message sent!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageText.trim()) {
        sendMessage(messageText);
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      sendMessage(messageText, file);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return diffInMinutes < 1 ? 'now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return diffInDays === 1 ? 'yesterday' : `${diffInDays}d ago`;
    }
  };

  const Message = ({ message, isOwn }) => {
    const formatMessageTime = (timestamp) => {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    const renderMessageContent = () => {
      if (message.fileUrl || message.content?.fileUrl) {
        const fileUrl = message.fileUrl || message.content?.fileUrl;
        const fileName = message.fileName || message.content?.fileName || 'Unknown file';
        const fileType = message.fileType || message.content?.fileType || '';

        if (fileType.startsWith('image/')) {
          return (
            <div className="space-y-2">
              {message.text && message.text !== `Sent a photo` && (
                <p className="text-sm">{message.text}</p>
              )}
              <div 
                className="relative cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setSelectedImage(fileUrl)}
              >
                <img 
                  src={fileUrl} 
                  alt={fileName}
                  className="max-w-[200px] sm:max-w-xs rounded-lg shadow-sm"
                  style={{ maxHeight: '200px' }}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 bg-black bg-opacity-20 rounded-lg transition-opacity">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          );
        } else {
          return (
            <div className="space-y-2">
              {message.text && message.text !== `Sent a file` && (
                <p className="text-sm">{message.text}</p>
              )}
              <a 
                href={fileUrl} 
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg bg-slate-50 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{fileName}</p>
                  <p className="text-xs text-gray-500">Click to download</p>
                </div>
                <img 
                  src="/asset/ForBusiness/download-svgrepo-com.svg" 
                  alt="Download" 
                  className="w-5 h-5 text-gray-400"
                />
              </a>
            </div>
          );
        }
      }

      return <p className="text-sm">{message.text}</p>;
    };

    return (
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-3 sm:mb-4 px-1`}>
        <div className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-2 rounded-lg ${
          isOwn
            ? 'bg-[#FF8C00] text-white rounded-br-none'
            : 'bg-gray-200 text-gray-800 rounded-bl-none'
        }`}>
          <div className="text-left">
            {renderMessageContent()}
          </div>
          <p className={`text-xs mt-1 ${isOwn ? 'text-orange-100' : 'text-gray-500'}`}>
            {formatMessageTime(message.timestamp)}
          </p>
        </div>
      </div>
    );
  };

  const filteredChats = chats.filter(chat => {
    // Search filter
    if (searchTerm && !chat.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Status filter
    if (filter === 'applicants' && !chat.isApplicant) {
      console.log(`🚫 [Chat] Filtering out ${chat.name} (not applicant) for applicants filter`);
      return false;
    }
    if (filter === 'others' && chat.isApplicant) {
      console.log(`🚫 [Chat] Filtering out ${chat.name} (is applicant) for others filter`);
      return false;
    }
    
    return true;
  });

  // Debug filtered results
  console.log(`🔍 [Chat] Filter state: "${filter}", showing ${filteredChats.length}/${chats.length} chats`);
  console.log(`📋 [Chat] Filtered chats:`, filteredChats.map(chat => ({
    name: chat.name,
    isApplicant: chat.isApplicant
  })));

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-50 p-3 sm:p-6 h-screen overflow-hidden">
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        <div className="flex justify-between items-center mb-4 sm:mb-6 flex-shrink-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Business Messages</h1>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs text-gray-500">{chats.length} conversations</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 flex-1 min-h-0">
          <div className={`lg:col-span-1 h-full ${selectedChat ? 'hidden lg:block' : 'block'}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden h-full flex flex-col">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h2 className="font-semibold text-gray-800 text-sm sm:text-base">Messages</h2>
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                    <span className="text-xs text-gray-500">{filteredChats.length} online</span>
                  </div>
                </div>
                
                <div className="relative mb-3 sm:mb-4">
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent"
                  />
                  <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                <div className="flex space-x-1 overflow-x-auto">
                  {(() => {
                    const tabs = [
                      { key: 'all', label: 'All', count: chats.length },
                      { key: 'applicants', label: 'Applicants', count: chats.filter(c => c.isApplicant).length },
                      { key: 'others', label: 'Others', count: chats.filter(c => !c.isApplicant).length }
                    ];
                    
                    console.log(`🏷️ [Chat] Filter tabs:`, tabs.map(tab => `${tab.label}: ${tab.count}`));
                    
                    return tabs;
                  })().map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        console.log(`🔄 [Chat] Filter changed from "${filter}" to "${tab.key}"`);
                        setFilter(tab.key);
                      }}
                      className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                        filter === tab.key
                          ? 'bg-orange-100 text-[#FF8C00]'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label} ({tab.count})
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto min-h-0">
                {filteredChats.length === 0 ? (
                  <div className="p-4 sm:p-6 text-left text-gray-500">
                    <svg className="w-10 sm:w-12 h-10 sm:h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p className="text-sm sm:text-base font-medium mb-1">No conversations yet</p>
                    <p className="text-xs sm:text-sm">Start chatting with job applicants and candidates</p>
                  </div>
                ) : (
                  filteredChats.map(chat => {
                    const isSelected = selectedChat?.id === chat.id;
                    const unreadCount = chat.unreadCount?.[currentUser.uid] || 0;

                    return (
                      <div
                        key={chat.id}
                        onClick={() => {
                          setSelectedChat(chat);
                          // Mark as read immediately when chat is selected
                          if (unreadCount > 0) {
                            markAsRead(chat.id);
                          }
                        }}
                        className={`p-3 sm:p-4 border-b border-gray-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                          isSelected ? 'bg-orange-50 border-r-4 border-r-[#FF8C00]' : ''
                        } ${unreadCount > 0 ? 'bg-blue-50' : ''}`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="relative flex-shrink-0">
                            <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-gray-200 overflow-hidden relative">
                              {chat.avatar ? (
                                <>
                                  <img 
                                    src={chat.avatar} 
                                    alt={chat.name} 
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      console.log(`❌ [Chat] Failed to load avatar for ${chat.name}: ${chat.avatar}`);
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div 
                                    className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 text-white font-semibold text-sm sm:text-lg absolute inset-0"
                                    style={{display: 'none'}}
                                  >
                                    {chat.name?.charAt(0)?.toUpperCase() || '?'}
                                  </div>
                                </>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 text-white font-semibold text-sm sm:text-lg">
                                  {chat.name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                              )}
                            </div>
                            {chat.isApplicant && (
                              <div className="absolute -bottom-1 -right-1 w-3 sm:w-4 h-3 sm:h-4 bg-blue-500 rounded-full border-2 border-white">
                                <svg className="w-1.5 sm:w-2 h-1.5 sm:h-2 text-white m-0.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center space-x-1 sm:space-x-2 flex-1 min-w-0">
                                <h3 className={`font-medium truncate text-left text-sm sm:text-base ${unreadCount > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {chat.name}
                                </h3>
                                {chat.isApplicant && (
                                  <span className="px-1 sm:px-1.5 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 hidden sm:inline">
                                    Applicant
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center space-x-1 sm:space-x-2">
                                {chat.lastMessageTimestamp && (
                                  <span className="text-xs text-gray-500 flex-shrink-0">
                                    {formatTime(chat.lastMessageTimestamp)}
                                  </span>
                                )}
                                {unreadCount > 0 && (
                                  <div className="bg-[#FF8C00] text-white text-xs rounded-full px-1.5 sm:px-2 py-1 min-w-[18px] sm:min-w-[20px] flex items-center justify-center">
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {chat.isApplicant && userProfiles[chat.otherUserId]?.applicationContext && (
                              <p className="text-xs text-blue-600 truncate mb-1 text-left hidden sm:block">
                                📄 Applied for: {userProfiles[chat.otherUserId].applicationContext.positions}
                              </p>
                            )}
                            
                            <p className={`text-xs sm:text-sm truncate text-left ${unreadCount > 0 ? 'text-gray-600 font-medium' : 'text-gray-500'}`}>
                              {chat.lastMessage || 'No messages yet'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className={`lg:col-span-2 h-full ${selectedChat ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden h-full flex flex-col">
              {!selectedChat ? (
                <div className="h-full flex items-center justify-center bg-slate-50">
                  <div className="text-center text-gray-500 p-4">
                    <svg className="w-16 sm:w-24 h-16 sm:h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h3 className="text-lg sm:text-xl font-semibold mb-2">Select a conversation</h3>
                    <p className="text-sm sm:text-base">Choose a conversation from the sidebar to start messaging</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="p-3 sm:p-4 border-b border-gray-200 bg-white flex-shrink-0">
                    <div className="flex items-start space-x-3">
                      {/* Mobile back button */}
                      <button
                        onClick={() => setSelectedChat(null)}
                        className="lg:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <div className="w-10 sm:w-12 h-10 sm:h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0 relative">
                        {selectedChat.avatar ? (
                          <>
                            <img 
                              src={selectedChat.avatar} 
                              alt={selectedChat.name} 
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                console.log(`❌ [Chat] Failed to load header avatar for ${selectedChat.name}: ${selectedChat.avatar}`);
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div 
                              className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 text-white font-semibold text-sm sm:text-base absolute inset-0"
                              style={{display: 'none'}}
                            >
                              {selectedChat.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-400 to-orange-600 text-white font-semibold text-sm sm:text-base">
                            {selectedChat.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 truncate text-left text-sm sm:text-base">{selectedChat.name}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            selectedChat.isApplicant 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {selectedChat.isApplicant ? 'Job Applicant' : 'Contact'}
                          </span>
                        </div>
                        
                        <div className="mt-1 space-y-1">
                          {selectedChat.email && (
                            <p className="text-sm text-gray-600 flex items-center text-left">
                              <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                              </svg>
                              {selectedChat.email}
                            </p>
                          )}
                          
                          {selectedChat.isApplicant && userProfiles[selectedChat.otherUserId]?.applicationContext && (
                            <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                              <div className="flex items-center space-x-2">
                                <div className="w-5 h-5 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-blue-900 text-left">
                                    Applied for: {userProfiles[selectedChat.otherUserId].applicationContext.positions}
                                  </p>
                                  <p className="text-xs text-blue-700 text-left">
                                    {userProfiles[selectedChat.otherUserId].applicationContext.totalApplications} application(s) to your business
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {selectedChat.isApplicant && userProfiles[selectedChat.otherUserId]?.skills && (
                            <div className="mt-2">
                              <p className="text-xs text-gray-500 mb-1 text-left">Skills:</p>
                              <div className="flex flex-wrap gap-1">
                                {(() => {
                                  // Handle different types of skills data (string, array, etc.)
                                  const skillsData = userProfiles[selectedChat.otherUserId].skills;
                                  let skillsArray = [];
                                  
                                  if (Array.isArray(skillsData)) {
                                    skillsArray = skillsData;
                                  } else if (typeof skillsData === 'string') {
                                    skillsArray = skillsData.split(',');
                                  } else if (skillsData) {
                                    skillsArray = String(skillsData).split(',');
                                  }
                                  
                                  return skillsArray.slice(0, 3).map((skill, index) => (
                                    <span 
                                      key={index}
                                      className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                                    >
                                      {String(skill).trim()}
                                    </span>
                                  ));
                                })()}
                                {(() => {
                                  // Handle skills count calculation
                                  const skillsData = userProfiles[selectedChat.otherUserId].skills;
                                  let skillsArray = [];
                                  
                                  if (Array.isArray(skillsData)) {
                                    skillsArray = skillsData;
                                  } else if (typeof skillsData === 'string') {
                                    skillsArray = skillsData.split(',');
                                  } else if (skillsData) {
                                    skillsArray = String(skillsData).split(',');
                                  }
                                  
                                  return skillsArray.length > 3 && (
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                      +{skillsArray.length - 3} more
                                    </span>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3 sm:p-4 min-h-0" style={{ minHeight: '200px' }}>
                    {messages.length === 0 ? (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center text-gray-500">
                          <svg className="w-12 sm:w-16 h-12 sm:h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <p className="text-sm sm:text-base">Start the conversation!</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {messages.map((message) => (
                          <Message
                            key={message.id}
                            message={message}
                            isOwn={message.senderId === currentUser.uid}
                          />
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  <div className="p-3 sm:p-4 border-t border-gray-200 flex-shrink-0">
                    <div className="flex items-end space-x-2 sm:space-x-3">
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 text-gray-500 hover:text-[#FF8C00] transition-colors flex-shrink-0"
                        title="Attach file"
                      >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <textarea
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          onKeyDown={handleKeyPress}
                          placeholder="Type a message..."
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#FF8C00] focus:border-transparent text-sm sm:text-base"
                          rows="1"
                          style={{ minHeight: '40px', maxHeight: '120px' }}
                        />
                      </div>
                      
                      <button
                        onClick={() => sendMessage(messageText)}
                        disabled={(!messageText.trim() && !fileInputRef.current?.files[0]) || sending}
                        className="p-2 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                      >
                        {sending ? (
                          <div className="w-5 h-5 sm:w-6 sm:h-6 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        ) : (
                          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        )}
                      </button>
                    </div>
                    
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/*,.pdf,.doc,.docx,.txt"
                      className="hidden"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setSelectedImage(null)}>
          <div className="max-w-4xl max-h-full p-4">
            <img src={selectedImage} alt="Full size" className="max-w-full max-h-full object-contain" />
          </div>
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default BusinessChat; 